import { generatePath } from "react-router-dom"
import { Context } from "../types"
import { getSubjectPath } from "../../../study-plans/helpers"
import { StudyPlanTargets } from "../../../machines/studyPlan/types"

// Helper function to determine subpage based on state
const determineSubpage = (stateName: string): string | undefined => {
  // Map xState states to URL subpages
  switch (stateName) {
    case "review_plan":
      return "view"
    case "edit_plan":
      return "edit"
    case "study_plans_scores":
      return "scores"
    case "teacher_view":
      return undefined // No subpage for teacher view
    case "study_plans_overview":
      return undefined // No subpage for overview
    case "landing_view":
      return undefined // No subpage for landing
    default:
      return undefined
  }
}

// Helper function to determine entityType for new navigation
const determineEntityType = (
  ctx: Context,
  stateName: string
): string | undefined => {
  // Check if viewing recommended plan
  if (
    stateName === "review_plan" &&
    ctx.currentTarget === StudyPlanTargets.ViewRecommended
  ) {
    return "recommended"
  }
  return undefined
}

export const setRedirectUrl = (
  ctx,
  _evt,
  meta: any,
  currentTarget: StudyPlanTargets
) => {
  // Don't redirect if no program or if we're in before_login state
  if (!ctx.program || meta.state?.value === "before_login") {
    return {}
  }

  // Handle nested states - get the top-level state name
  let stateName: string
  if (typeof meta.state?.value === "string") {
    stateName = meta.state.value
  } else if (typeof meta.state?.value === "object") {
    // For nested states, get the parent state name
    stateName = Object.keys(meta.state.value)[0]
  } else {
    // Fallback - try to determine from context
    if (currentTarget === StudyPlanTargets.ReviewPlan) {
      stateName = "review_plan"
    } else if (currentTarget === StudyPlanTargets.Edit) {
      stateName = "edit_plan"
    } else if (currentTarget === StudyPlanTargets.TeacherView) {
      stateName = "teacher_view"
    } else if (currentTarget === StudyPlanTargets.Scores) {
      stateName = "study_plans_scores"
    } else if (currentTarget === StudyPlanTargets.Overview) {
      stateName = "study_plans_overview"
    } else if (currentTarget === StudyPlanTargets.Landing) {
      stateName = "landing_view"
    } else {
      return {} // Can't determine state
    }
  }

  if (ctx.isOldNav) {
    // Old navigation: URL includes program and subject
    const path = generatePath("/eoc/:program/study-plans/:subject?/:subpage?", {
      program: ctx.program.toLowerCase(),
      subject:
        stateName !== "landing_view" && ctx.subject
          ? getSubjectPath(ctx.subject)
          : undefined,
      subpage: determineSubpage(stateName)
    })

    // Remove trailing slashes and clean up path
    const cleanPath = path.replace(/\/+$/, "").replace(/\/+/g, "/")

    console.log("[setRedirectUrl] Generated path:", cleanPath)

    return {
      redirectUrl: {
        path: cleanPath,
        action: (meta?.action?.action ?? "push") as "push" | "replace"
      }
    }
  } else {
    // New navigation: URL doesn't include program/subject (uses filters)
    const path = generatePath(
      "/reporting/teaching-tools/study-plan/:subpage?/:entityType?",
      {
        subpage: determineSubpage(stateName),
        entityType: determineEntityType(ctx, stateName)
      }
    )

    // Remove trailing slashes and clean up path
    const cleanPath = path.replace(/\/+$/, "").replace(/\/+/g, "/")

    return {
      currentTarget: currentTarget,
      redirectUrl: {
        path: cleanPath,
        action: (meta?.action?.action ?? "push") as "push" | "replace"
      }
    }
  }
}
